import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { PAGE_SIZE, type SortKey, type SubscriberPayload, type SubscriberRow } from "./types";

export function useSubscriberRoster(environment: "sandbox" | "live") {
  const [data, setData] = useState<SubscriberPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [tierFilter, setTierFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [cycleFilter, setCycleFilter] = useState<string>("all");
  const [sortKey, setSortKey] = useState<SortKey>("mrr_cents");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    const { data: payload, error } = await supabase.functions.invoke(
      "admin-pnl-subscribers",
      { body: { environment } },
    );
    setLoading(false);
    if (error) {
      toast.error(`Could not load roster: ${error.message}`);
      return;
    }
    setData(payload as SubscriberPayload);
    setPage(1);
  }, [environment]);

  useEffect(() => {
    void load();
  }, [load]);

  const rows = useMemo(() => data?.rows ?? [], [data?.rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (tierFilter !== "all" && (r.account_tier ?? "") !== tierFilter) return false;
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (cycleFilter !== "all" && r.cycle !== cycleFilter) return false;
      if (q) {
        const hay = `${r.email ?? ""} ${r.username ?? ""} ${r.display_name ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, search, tierFilter, statusFilter, cycleFilter]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      let av: string | number = "";
      let bv: string | number = "";
      switch (sortKey) {
        case "email": av = a.email ?? ""; bv = b.email ?? ""; break;
        case "tier": av = a.account_tier ?? ""; bv = b.account_tier ?? ""; break;
        case "cycle": av = a.cycle; bv = b.cycle; break;
        case "status": av = a.status; bv = b.status; break;
        case "current_period_end": av = a.current_period_end ?? ""; bv = b.current_period_end ?? ""; break;
        case "mrr_cents": av = a.mrr_cents; bv = b.mrr_cents; break;
        case "joined_at": av = a.joined_at ?? ""; bv = b.joined_at ?? ""; break;
      }
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const pageRows = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const kpis = useMemo(() => {
    let active = 0, trialing = 0, pastDue = 0, cancelingAtEnd = 0, mrr = 0;
    for (const r of rows) {
      if (r.status === "active") active += 1;
      else if (r.status === "trialing") trialing += 1;
      else if (r.status === "past_due") pastDue += 1;
      if (r.cancel_at_period_end && r.status === "active") cancelingAtEnd += 1;
      mrr += r.mrr_cents;
    }
    return { active, trialing, pastDue, cancelingAtEnd, mrr, arr: mrr * 12 };
  }, [rows]);

  const toggleSort = (k: SortKey) => {
    if (sortKey === k) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(k);
      setSortDir(k === "mrr_cents" ? "desc" : "asc");
    }
  };

  return {
    data, loading, search, setSearch,
    tierFilter, setTierFilter, statusFilter, setStatusFilter,
    cycleFilter, setCycleFilter, sortKey, sortDir, toggleSort,
    page, setPage, totalPages, rows, sorted, pageRows, kpis, load,
  };
}

export function exportRosterCsv(sorted: SubscriberRow[], environment: string) {
  if (!sorted.length) return;
  const header = [
    "user_id", "email", "username", "display_name", "tier", "cycle", "status",
    "cancel_at_period_end", "current_period_end", "joined_at",
    "unit_amount_usd", "mrr_usd", "price_id", "stripe_subscription_id",
  ];
  const rowsCsv = sorted.map((r) => [
    r.user_id, r.email ?? "", r.username ?? "", r.display_name ?? "",
    r.account_tier ?? "", r.cycle, r.status,
    r.cancel_at_period_end ? "true" : "false",
    r.current_period_end ?? "", r.joined_at ?? "",
    (r.unit_amount_cents / 100).toFixed(2),
    (r.mrr_cents / 100).toFixed(2),
    r.price_id, r.stripe_subscription_id,
  ]);
  const csv = [header, ...rowsCsv]
    .map((r) => r.map((c) => {
      let v = String(c ?? "");
      if (/^[=+\-@\t\r]/.test(v)) v = `'${v}`;
      return `"${v.replace(/"/g, '""')}"`;
    }).join(","))
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `subscribers-${environment}-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}
